import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useSubjectPicker } from '@/hooks/usePickers';
import { academicSubjectsApi, gradeOfferingsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';

/**
 * What a class is taught, and how much of each subject.
 *
 * Attached to the grade offering rather than the grade, so editing this year's
 * curriculum leaves last year's exactly as it was. The weekly period figure is
 * the intention; the timetable is the operative schedule, and keeping them apart
 * is what makes "we planned five but scheduled three" answerable.
 */
export function CurriculumPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const subjects = useSubjectPicker();
  const [offeringId, setOfferingId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ subjectId: '', mandatory: true, weeklyPeriods: '' });

  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ size: 200 }),
    queryFn: () => gradeOfferingsApi.list({ page: 0, size: 200 }),
  });

  const query = useQuery({
    queryKey: queryKeys.academicSubjects.forOffering(offeringId),
    queryFn: () => academicSubjectsApi.list(offeringId),
    enabled: offeringId !== '',
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.academicSubjects.all });

  const add = useMutation({
    mutationFn: () =>
      academicSubjectsApi.create({
        gradeOfferingId: offeringId,
        subjectId: form.subjectId,
        mandatory: form.mandatory,
        weeklyPeriods: form.weeklyPeriods === '' ? undefined : Number(form.weeklyPeriods),
      }),
    onSuccess: () => {
      toast.success('Subject added to the curriculum');
      setFormOpen(false);
      setForm({ subjectId: '', mandatory: true, weeklyPeriods: '' });
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      academicSubjectsApi.changeStatus(id, status),
    onSuccess: () => {
      toast.success('Curriculum updated');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const selected = offerings.data?.data.find((offering) => offering.id === offeringId);
  const plannedPeriods = query.data?.reduce((total, entry) => total + (entry.weeklyPeriods ?? 0), 0) ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Curriculum"
        description="The subjects each class is taught this year. Last year's curriculum is untouched by changes here."
        action={
          can(PERMISSIONS.ACADEMIC_SUBJECT_CREATE) && offeringId !== '' ? (
            <Button onClick={() => setFormOpen(true)}>Add subject</Button>
          ) : undefined
        }
      />

      <Card className="p-5">
        <div className="mb-4 max-w-md">
          <SelectField
            label="Class"
            value={offeringId}
            onChange={(event) => setOfferingId(event.target.value)}
          >
            <option value="">Select a class…</option>
            {offerings.data?.data.map((offering) => (
              <option key={offering.id} value={offering.id}>
                {offering.academicYear.name} · {offering.grade.name}
                {offering.campusName !== undefined && ` · ${offering.campusName}`}
              </option>
            ))}
          </SelectField>
        </div>

        {offeringId === '' ? (
          <p className="py-6 text-center text-sm text-subtle">
            Choose a class to see and edit its curriculum.
          </p>
        ) : query.isPending ? (
          <LoadingState label="Loading curriculum" />
        ) : query.error !== null ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data === undefined || query.data.length === 0 ? (
          <p className="py-6 text-center text-sm text-subtle">
            No subjects on this class’s curriculum yet.
          </p>
        ) : (
          <>
            {selected?.historical === true && (
              <p className="mb-3 rounded-lg bg-surface-2 px-3 py-2 text-xs text-muted">
                This academic year has finished, so its curriculum is read-only.
              </p>
            )}

            <ul className="divide-y divide-line">
              {query.data.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-3 py-3">
                  <div>
                    <p className="text-sm font-medium text-ink">
                      {entry.subjectName}
                      <span className="ml-2 font-mono text-xs text-subtle">{entry.subjectCode}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-subtle">
                      {entry.weeklyPeriods === undefined
                        ? 'No weekly allocation set'
                        : `${entry.weeklyPeriods} periods a week`}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {entry.mandatory ? (
                      <Badge tone="neutral">Mandatory</Badge>
                    ) : (
                      <Badge tone="info">Elective</Badge>
                    )}
                    {entry.status === 'INACTIVE' && <Badge tone="neutral">Inactive</Badge>}
                    {can(PERMISSIONS.ACADEMIC_SUBJECT_UPDATE) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={changeStatus.isPending}
                        onClick={() =>
                          changeStatus.mutate({
                            id: entry.id,
                            status: entry.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                          })
                        }
                      >
                        {entry.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <p className="mt-4 text-xs text-subtle">
              {plannedPeriods} periods a week planned across {query.data.length} subjects.
            </p>
          </>
        )}
      </Card>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="Add a subject"
        description="The subject itself is defined once for the school; what varies is how much of it this class is taught."
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={add.isPending}>
              Cancel
            </Button>
            <Button onClick={() => add.mutate()} loading={add.isPending} disabled={form.subjectId === ''}>
              Add subject
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <SelectField
            label="Subject"
            required
            value={form.subjectId}
            onChange={(event) => setForm({ ...form, subjectId: event.target.value })}
          >
            <option value="">Select a subject…</option>
            {subjects.data
              ?.filter(
                (subject) => query.data?.some((entry) => entry.subjectId === subject.id) !== true,
              )
              .map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name} · {subject.code}
                </option>
              ))}
          </SelectField>

          <TextField
            label="Periods a week"
            type="number"
            min={1}
            value={form.weeklyPeriods}
            onChange={(event) => setForm({ ...form, weeklyPeriods: event.target.value })}
            hint="The intention. The timetable is what actually gets scheduled."
          />

          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 border-line text-accent focus:ring-accent"              checked={form.mandatory}
              onChange={(event) => setForm({ ...form, mandatory: event.target.checked })}
            />
            Every student takes this subject
          </label>
        </div>
      </Modal>
    </div>
  );
}
